import { IntlMessageFormat } from "intl-messageformat";

// The strings table (decision 0061: "every player-facing string goes through a strings table by ID, with ICU message
// formatting for plurals"). Rows are `{ id, text }`; `text` is an ICU message ("{count, plural, one {# glyph} other
// {# glyphs}}"). `t(id, params)` formats one. A missing id renders as a visible marker and logs once, so it can't
// pass for a real string; the raw-string scan flags it too.

/** One row of a strings table. */
export interface StringRow {
  id: string;
  /** The ICU message. */
  text: string;
}

/** Values a message's placeholders take. */
export type MessageParams = Record<string, string | number | boolean | Date | null | undefined>;

/** The formatter a game's UI calls. */
export interface Strings {
  /** Format a string by id. A missing id returns `⟦id⟧` and logs once. */
  t(id: string, params?: MessageParams): string;
  /** Does the table have this id? */
  has(id: string): boolean;
  /** Every id. */
  ids(): string[];
  /** Every message's source text (what the raw-string scan matches against). */
  messages(): string[];
  /** Ids asked for but missing, in first-ask order. */
  missing(): string[];
}

/** The marker a missing id renders as. */
export function missingMarker(id: string): string {
  return `⟦${id}⟧`;
}

/** A row check for the registry: rejects a strings row whose message doesn't parse. */
export function checkMessage(row: { id: string } & Record<string, unknown>): string | null {
  if (typeof row.text !== "string") return "text is not a string";
  try {
    new IntlMessageFormat(row.text, "en");
    return null;
  } catch (e) {
    return `message does not parse: ${e instanceof Error ? e.message : String(e)}`;
  }
}

/** Build the formatter over a strings table's rows. `log` defaults to console.error. */
export function createStrings(rows: readonly StringRow[], o: { locale?: string; log?: (line: string) => void } = {}): Strings {
  const locale = o.locale ?? "en";
  const log = o.log ?? ((line: string) => console.error(line));
  const text = new Map(rows.map((r) => [r.id, r.text]));
  const compiled = new Map<string, IntlMessageFormat>();
  const missing: string[] = [];
  return {
    t(id, params) {
      const src = text.get(id);
      if (src === undefined) {
        if (!missing.includes(id)) {
          missing.push(id);
          log(`[strings] no string "${id}"`);
        }
        return missingMarker(id);
      }
      let mf = compiled.get(id);
      if (!mf) {
        mf = new IntlMessageFormat(src, locale);
        compiled.set(id, mf);
      }
      return String(mf.format(params));
    },
    has: (id) => text.has(id),
    ids: () => [...text.keys()],
    messages: () => [...text.values()],
    missing: () => [...missing],
  };
}

// --- The raw-string scan -------------------------------------------------------------------------------------------

// The parser's element kinds (@formatjs/icu-messageformat-parser TYPE), read structurally.
const LITERAL = 0, ARGUMENT = 1, NUMBER = 2, DATE = 3, TIME = 4, SELECT = 5, PLURAL = 6, POUND = 7, TAG = 8;
interface AstNode {
  type: number;
  value?: string;
  options?: Record<string, { value: AstNode[] }>;
  children?: AstNode[];
}

const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const squash = (s: string): string => s.replace(/\s+/g, " ").trim();

function toPattern(nodes: readonly AstNode[]): string {
  return nodes
    .map((n) => {
      switch (n.type) {
        case LITERAL:
          return escape(n.value ?? "").replace(/\s+/g, "\\s*");
        case ARGUMENT:
        case NUMBER:
        case DATE:
        case TIME:
        case POUND:
          return "[\\s\\S]+?";
        case SELECT:
        case PLURAL:
          return `(?:${Object.values(n.options ?? {})
            .map((o) => toPattern(o.value))
            .join("|")})`;
        case TAG:
          return toPattern(n.children ?? []);
        default:
          return "[\\s\\S]*?";
      }
    })
    .join("");
}

/** The default texts a scan lets through: numbers, times and punctuation (a HUD value, a timer). */
export const DEFAULT_ALLOWED: readonly RegExp[] = [/^[\d\s.,:;%+\-–—/×x()[\]#]*$/];

/**
 * Build a scan over a strings table's messages. The scan takes the texts a screen shows (one per text node, as the UI
 * sets them from `t()`) and returns those no message could have produced: raw strings that bypassed the table.
 * Placeholders match any text, plural and select branches each match, whitespace is loose; `allow` adds patterns
 * to let through (numbers and punctuation by default).
 */
export function createRawStringScan(messages: Iterable<string>, o: { allow?: readonly RegExp[] } = {}): (texts: readonly string[]) => string[] {
  const patterns: RegExp[] = [];
  for (const m of messages) {
    const ast = new IntlMessageFormat(m, "en").getAst() as unknown as AstNode[];
    patterns.push(new RegExp(`^\\s*${toPattern(ast)}\\s*$`, "u"));
  }
  const allow = o.allow ?? DEFAULT_ALLOWED;
  return (texts) =>
    texts
      .map(squash)
      .filter((s) => s !== "" && !allow.some((a) => a.test(s)) && !patterns.some((p) => p.test(s)));
}
